import React, { useEffect, useState } from "react";
import { Alert, Box, TextField, Typography, CircularProgress } from "@mui/material";
import { IniInfo, UnconfiguredRules } from "system/types/interfaces";
import { ConfiguredTable } from "../configurationTab/ConfiguredTable";
import { NotConfiguredTable } from "../configurationTab/NotConfiguredTable";
import { AddConfigRuleModal } from "../configurationTab/AddConfigRuleModal";
import { ConfigRuleApplicabilityModal } from "../configurationTab/ConfigRuleApplicabilityModal";
import { useApi, useConfiguration } from "system/hooks";
import { useDispatch, useSelector } from "react-redux";
import { policyEditorActions } from "system/store/slices/policy-editor-slice";
import { findLastIndexManual } from "system/libraries/utilities";
import { MatchingPackagesModal } from "./MatchingPackagesModal";
import { defaultConfigurationsForNotConfigured } from "components/modules/policy-editor/constants/defaultConfigurationsForNotConfigured";
import { InheritedConfigModal } from "./InheritedConfigModal";
import { RootState } from "system/store";
import { CommandBar, ICommandBarItemProps } from "@fluentui/react";
import { ConfirmationDialog } from "components/ui";
import { normalizeAuthoredConfigValue } from "components/modules/policy-editor/utils/configValueQuoting";
import { toggleCommentRow } from "components/modules/policy-editor/utils/configCommentRows";

interface InheritedConfigurationTabProps {
  id: number;
  regex: string;
  matchingPackages: string[];
  configurations: IniInfo[];
  notConfiguredRules: UnconfiguredRules[];
  readOnlyViewer: boolean;
  isDeleted?: boolean;
}

export const InheritedConfigurationTab: React.FC<InheritedConfigurationTabProps> = ({
  id,
  regex,
  matchingPackages,
  configurations,
  notConfiguredRules,
  readOnlyViewer,
  isDeleted,
}) => {
  const dispatch = useDispatch();
  const api = useApi();
  const currentTenant = useConfiguration().currentTenant;
  const selectedPackage = useSelector((state: RootState) => state.policyEditor.selectedPackage);
  const [loading, setLoading] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [insertIndex, setInsertIndex] = useState<number | null>(null);
  const [showApplicabilityModal, setShowApplicabilityModal] = useState(false);
  const [draftConfigRule, setDraftConfigRule] = useState<IniInfo | null>(null);
  const [selectedUnconfigured, setSelectedUnconfigured] = useState<UnconfiguredRules | null>(null);
  const [isMatchingModalOpen, setIsMatchingModalOpen] = useState(false);
  const [isRegexModalOpen, setIsRegexModalOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  useEffect(() => {
    if (notConfiguredRules.length === 0 && matchingPackages.length > 0 && currentTenant?.shortName) {
      setLoading(true);
      setIsMatchingModalOpen(true);
      api.policyEditor
        .getInheritedConfigurations(currentTenant.shortName, matchingPackages)
        .then((response) => {
          if (response.isSuccess && response.items) {
            const updatedRules = [...defaultConfigurationsForNotConfigured, ...response.items];
            dispatch(policyEditorActions.updateInheritedNotConfigured({ id, notConfigured: updatedRules }));
          } else {
            dispatch(policyEditorActions.updateInheritedNotConfigured({ id, notConfigured: defaultConfigurationsForNotConfigured }));
          }
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
        });
    }
  }, [notConfiguredRules.length, matchingPackages, api.policyEditor, dispatch, currentTenant?.shortName, id]);

  const handleRegexUpdated = async (newRegex: string, newMatchingPackages: string[]) => {
    setLoading(true);
    dispatch(policyEditorActions.updateInheritedRegex({ id, newRegex, newMatchingPackages }));
    try {
      const response = await api.policyEditor.getInheritedConfigurations(currentTenant?.shortName ?? "", newMatchingPackages);
      if (response.isSuccess && response.items) {
        const updatedNotConfigured = [...defaultConfigurationsForNotConfigured, ...response.items];
        dispatch(policyEditorActions.updateInheritedNotConfigured({ id, notConfigured: updatedNotConfigured }));

        const newNotConfiguredNames = new Set(updatedNotConfigured.map((r) => r.name));
        configurations.forEach((config) => {
          if (!newNotConfiguredNames.has(config.name)) {
            dispatch(policyEditorActions.deleteInheritedConfigurationItem({ id, itemId: config.id ?? "" }));
          }
        });
      }
    } catch (error) {
      console.error("Error fetching new notConfigured:", error);
    } finally {
      setLoading(false);
      setIsMatchingModalOpen(true);
    }
  };

  const handleAddRule = (newRule: {
    rule: string;
    applicability: string;
    value: string;
    description: string;
    type: string;
    regex?: string;
    valueShouldBeLogged?: boolean;
  }) => {
    const draft: IniInfo = {
      id: Date.now().toString(),
      name: newRule.rule,
      value: newRule.value,
      applicability: "endpoint",
      delimiter: "is for any",
      pendingChanges: true,
      description: newRule.description,
      type: newRule.type,
      regex: newRule.regex,
      valueShouldBeLogged: newRule.valueShouldBeLogged,
    };
    setDraftConfigRule(draft);
    setIsAddModalOpen(false);
    setSelectedUnconfigured(null);
    setShowApplicabilityModal(true);
  };

  const handleApplicabilityConfirmed = (finalRule: IniInfo) => {
    finalRule.value =
      normalizeAuthoredConfigValue(finalRule.name ?? "", finalRule.value, finalRule.delimiter ?? "", finalRule.applicability ?? "") ??
      finalRule.value;

    const existingIndex = configurations.findIndex((x) => x.id === finalRule.id);

    if (existingIndex >= 0) {
      dispatch(policyEditorActions.updateInheritedConfigurationItem({ id, item: { ...finalRule, pendingChanges: true } }));
    } else {
      let position = insertIndex ?? configurations.length;
      if (finalRule.name === "Pre-run Command") {
        const lastPreRunIndex = findLastIndexManual(configurations, (line) => line.name === "Pre-run Command" && !line.isDeleted);
        position = lastPreRunIndex >= 0 ? lastPreRunIndex + 1 : 0;
      } else if (finalRule.name === "Post-run Command") {
        position = configurations.length;
      }
      dispatch(policyEditorActions.addInheritedConfigurationItem({ id, item: { ...finalRule, pendingChanges: true }, position }));
    }

    setInsertIndex(null);
    setShowApplicabilityModal(false);
    setDraftConfigRule(null);
  };

  const handleToggleComment = (itemId: string, isComment: boolean) => {
    const row = configurations.find((c) => c.id === itemId);
    if (!row) return;
    const toggled = toggleCommentRow(row, isComment);
    if (toggled) dispatch(policyEditorActions.updateInheritedConfigurationItem({ id, item: toggled }));
  };

  const handleOpenEditModal = (rule: IniInfo) => {
    setDraftConfigRule({ ...rule, value: rule.value.trim() });
    setShowApplicabilityModal(true);
  };

  const handleDeleteConfiguration = (itemId: string) => {
    dispatch(policyEditorActions.deleteInheritedConfigurationItem({ id, itemId }));
  };

  const handleUndoDeleteConfiguration = (itemId: string) => {
    dispatch(policyEditorActions.undoDeleteInheritedConfigurationItem({ id, itemId }));
  };

  const handleReorderConfigurations = (oldIndex: number, newIndex: number) => {
    dispatch(policyEditorActions.reorderInheritedConfigurationItems({ id, oldIndex, newIndex }));
  };

  const handleDeleteConfirmed = () => {
    dispatch(policyEditorActions.removeInheritedConfiguration({ id }));
    setIsDeleteConfirmOpen(false);
  };

  const handleRestore = () => {
    dispatch(policyEditorActions.restoreInheritedConfiguration({ id }));
  };

  const commandBarItems: ICommandBarItemProps[] = [
    {
      key: "viewMatching",
      text: `View ${matchingPackages.length} Matching Packages`,
      iconProps: { iconName: "View" },
      onClick: () => setIsMatchingModalOpen(true),
    },
    {
      key: "editRegex",
      text: "Edit Regex",
      disabled: readOnlyViewer || isDeleted,
      iconProps: { iconName: "Edit" },
      onClick: () => setIsRegexModalOpen(true),
    },
    isDeleted
      ? {
          key: "restore",
          text: "Undo Delete",
          disabled: readOnlyViewer,
          iconProps: { iconName: "Undo" },
          onClick: handleRestore,
        }
      : {
          key: "delete",
          text: "Delete",
          disabled: readOnlyViewer,
          iconProps: { iconName: "Delete" },
          onClick: () => setIsDeleteConfirmOpen(true),
        },
  ];

  return (
    <Box>
      <CommandBar
        items={commandBarItems}
        styles={{
          root: { backgroundColor: "inherit", padding: "8px" },
        }}
        className="bdr-bottom"
      />

      {isDeleted && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          This inherited configuration is marked for deletion. It will be removed on save and affects {matchingPackages.length} package
          {matchingPackages.length === 1 ? "" : "s"}. Click <strong>Undo Delete</strong> to restore.
        </Alert>
      )}

      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "start", gap: 2, mb: 3, mt: 5, opacity: isDeleted ? 0.5 : 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "500px" }}>
          <TextField
            fullWidth
            label="Regex Expression"
            value={regex}
            sx={{ maxWidth: "100%", width: "500px" }}
            variant="outlined"
            InputProps={{ readOnly: true }}
          />
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", mt: 10 }}>
          <CircularProgress />
          <Typography variant="body1" sx={{ ml: 2, color: "#fff" }}>
            Getting configurations for these packages...
          </Typography>
        </Box>
      ) : (
        <>
          <Typography variant="h6" mb={2} sx={{ color: "#fff" }}>
            Configured Rules
          </Typography>

          <ConfiguredTable
            configuredRules={configurations}
            onAdd={(index: number) => {
              setInsertIndex(index);
              setIsAddModalOpen(true);
            }}
            onDelete={handleDeleteConfiguration}
            onUndoDelete={handleUndoDeleteConfiguration}
            onEdit={handleOpenEditModal}
            onToggleComment={handleToggleComment}
            onReorder={handleReorderConfigurations}
            readOnlyViewer={readOnlyViewer}
          />
          <Typography variant="h6" sx={{ color: "#fff", mt: 3, mb: 1 }}>
            Default Configurations
          </Typography>
          <NotConfiguredTable
            notConfiguredRules={notConfiguredRules}
            readOnlyViewer={readOnlyViewer}
            onAdd={(ruleName) => {
              const found = notConfiguredRules.find((x) => x.name === ruleName);
              if (!found) return;
              setSelectedUnconfigured(found);
              setIsAddModalOpen(true);
            }}
          />

          <AddConfigRuleModal
            open={isAddModalOpen}
            onClose={() => {
              setSelectedUnconfigured(null);
              setIsAddModalOpen(false);
            }}
            onAddRule={handleAddRule}
            unConfiguredList={notConfiguredRules}
            initialItem={selectedUnconfigured}
          />
          {draftConfigRule && (
            <ConfigRuleApplicabilityModal
              open={showApplicabilityModal}
              onClose={() => {
                setShowApplicabilityModal(false);
                setDraftConfigRule(null);
              }}
              initialRule={draftConfigRule}
              onFinish={handleApplicabilityConfirmed}
            />
          )}
        </>
      )}
      <ConfirmationDialog
        title="Mark Inherited Configuration for Deletion"
        subText={`This config is shared by ${matchingPackages.length} package${
          matchingPackages.length === 1 ? "" : "s"
        }. After save, these settings will be removed from all of them. You can undo before saving.`}
        isVisible={isDeleteConfirmOpen}
        onConfirm={handleDeleteConfirmed}
        onCancel={() => setIsDeleteConfirmOpen(false)}
        confirmText="Mark for Deletion"
      >
        <Box sx={{ mt: 1, maxHeight: 280, overflow: "auto" }}>
          <Typography variant="caption" sx={{ color: "#A19F9D", display: "block", mb: 0.5 }}>
            Affected packages:
          </Typography>
          <Box component="ul" sx={{ m: 0, mb: 1.5, pl: 2.5, color: "white" }}>
            {matchingPackages.slice(0, 8).map((pkg) => (
              <li key={pkg}>
                <Typography variant="body2">{pkg}</Typography>
              </li>
            ))}
            {matchingPackages.length > 8 && (
              <li>
                <Typography variant="caption" sx={{ color: "#A19F9D" }}>
                  +{matchingPackages.length - 8} more
                </Typography>
              </li>
            )}
          </Box>
          {configurations.filter((c) => !c.isDeleted).length > 0 && (
            <>
              <Typography variant="caption" sx={{ color: "#A19F9D", display: "block", mb: 0.5 }}>
                Settings that will be removed ({configurations.filter((c) => !c.isDeleted).length}):
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5, color: "white" }}>
                {configurations
                  .filter((c) => !c.isDeleted)
                  .slice(0, 5)
                  .map((c) => (
                    <li key={c.id ?? c.name}>
                      <Typography variant="body2">{c.name}</Typography>
                    </li>
                  ))}
                {configurations.filter((c) => !c.isDeleted).length > 5 && (
                  <li>
                    <Typography variant="caption" sx={{ color: "#A19F9D" }}>
                      +{configurations.filter((c) => !c.isDeleted).length - 5} more
                    </Typography>
                  </li>
                )}
              </Box>
            </>
          )}
        </Box>
      </ConfirmationDialog>
      <InheritedConfigModal
        open={isRegexModalOpen}
        onClose={() => setIsRegexModalOpen(false)}
        subjectName={selectedPackage?.rule.name || ""}
        initialRegex={regex}
        mode="edit"
        onSubmit={handleRegexUpdated}
      />
      <MatchingPackagesModal open={isMatchingModalOpen} onClose={() => setIsMatchingModalOpen(false)} matchingPackages={matchingPackages} />
    </Box>
  );
};
